/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Home, ClipboardList, Sparkles, HeartHandshake, BookOpen, MessageCircle, Users, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { useAuth } from '@/lib/auth';

const STEPS = [
  { key: 'welcome', Icon: Sparkles, color: 'var(--wl-primary)' },
  { key: 'feed', Icon: Home, color: '#1D4ED8' },
  { key: 'bacheca', Icon: ClipboardList, color: '#B45309' },
  { key: 'ai', Icon: Sparkles, color: '#DB2777' },
  { key: 'wellness', Icon: HeartHandshake, color: '#DB2777' },
  { key: 'resources', Icon: BookOpen, color: '#7C3AED' },
  { key: 'chat', Icon: MessageCircle, color: 'var(--wl-primary-alt)' },
  { key: 'directory', Icon: Users, color: '#0EA5E9' },
];

const OnboardingModal = () => {
  const { t } = useTranslation();
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    // Skip onboarding for patients (they get a simpler onboarding flow later)
    if (user && user.has_onboarded === false && user.role !== 'patient') {
      // small delay so app shell is ready
      const t = setTimeout(() => setOpen(true), 400);
      return () => clearTimeout(t);
    }
  }, [user]);

  const complete = async (goTo) => {
    try { await updateProfile({ has_onboarded: true }); } catch {}
    setOpen(false);
    setStep(0);
    if (goTo) navigate(goTo);
  };

  const skip = () => complete(null);

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const stepGotoPath = {
    welcome: null, feed: '/feed', bacheca: '/bacheca', ai: '/ai',
    wellness: '/wellness', resources: '/resources', chat: '/chat', directory: '/directory'
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) skip(); else setOpen(true); }}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${current.color}18`, color: current.color }}>
              <current.Icon className="h-5 w-5" />
            </div>
            {t(`ob_${current.key}_title`)}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{t(`ob_${current.key}_desc`)}</p>
          <div className="flex items-center justify-center gap-1 pt-2">
            {STEPS.map((s, i) => (
              <div key={s.key} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-primary' : 'w-1.5 bg-muted'}`} />
            ))}
          </div>
        </div>
        <DialogFooter className="flex-row justify-between gap-2 sm:justify-between">
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={skip} data-testid="onboarding-skip">{t('ob_skip')}</Button>
            {step > 0 && <Button variant="secondary" size="sm" onClick={() => setStep(s => s - 1)} data-testid="onboarding-prev"><ChevronLeft className="h-4 w-4" /></Button>}
          </div>
          {isLast ? (
            <Button onClick={() => complete(stepGotoPath[current.key])} data-testid="onboarding-finish">
              <Check className="h-4 w-4 mr-1" /> {t('ob_finish')}
            </Button>
          ) : (
            <Button onClick={() => setStep(s => s + 1)} data-testid="onboarding-next">
              {t('ob_next')} <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;
